import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Check, CheckCircle2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toApiProblem } from '@/lib/api/client'
import { useAssistantProposal, useProposalMutations } from '../api'
import { PROPOSAL_DESTINATION } from '../types'
import type { AssistantProposalView } from '../types'

/**
 * A write the assistant wants to make, and the button that makes it.
 *
 * <p>This card is the governance rule made visible: the model produced a tool name, some
 * arguments and a sentence, and nothing has happened. Pressing Confirm calls the same
 * endpoint the ordinary screen calls, as the signed-in user, with the same permissions and
 * the same validation — so a proposal the person is not allowed to make is refused here
 * exactly as it would be refused there, and the refusal is shown on the card rather than
 * swallowed into the chat.
 *
 * <p>The arguments are listed in full. A summary alone would ask somebody to approve a write
 * on the strength of the model's own description of it.
 */
export function ProposalCard({
  proposal,
  onSettled,
}: {
  proposal: AssistantProposalView
  onSettled?: (updated: AssistantProposalView) => void
}) {
  const { t } = useTranslation()
  const { confirm, cancel } = useProposalMutations()
  const [decided, setDecided] = useState<AssistantProposalView | null>(null)
  const [error, setError] = useState<string | null>(null)

  // The stream gave a summary and an id; the arguments come from the record. Fetched only
  // when they are missing, so a card rebuilt from history does not ask again.
  const needsDetail = Object.keys(proposal.arguments ?? {}).length === 0 && decided === null
  const detail = useAssistantProposal(proposal.id, needsDetail)

  // Derived, not mirrored into state. What the person decided wins, then what the server
  // returned, then what the stream announced — and none of them needs an effect to copy it.
  const state = decided ?? detail.data ?? proposal

  const destination = PROPOSAL_DESTINATION[state.toolName]
  const pending = state.status === 'PENDING'
  const busy = confirm.isPending || cancel.isPending

  async function decide(action: 'confirm' | 'cancel') {
    setError(null)
    try {
      const updated = action === 'confirm'
        ? await confirm.mutateAsync(state.id)
        : await cancel.mutateAsync(state.id)
      setDecided(updated)
      onSettled?.(updated)
    } catch (problem) {
      setError(toApiProblem(problem)?.detail ?? t('assistant.proposal.failed'))
    }
  }

  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <p className="text-sm font-medium text-[var(--color-heading)]">
        {state.summary ?? t('assistant.proposal.untitled')}
      </p>

      {needsDetail && detail.isLoading && (
        <p className="mt-2 text-xs text-[var(--color-muted)]">{t('common.loading')}</p>
      )}

      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        {Object.entries(state.arguments ?? {}).map(([key, value]) => (
          <div key={key} className="contents">
            <dt className="text-[var(--color-muted)]">{humanise(key)}</dt>
            <dd className="min-w-0 break-words text-[var(--color-body)]">{render(value)}</dd>
          </div>
        ))}
      </dl>

      {pending && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={busy} onClick={() => void decide('confirm')}>
            <Check className="size-4" aria-hidden="true" />
            {t('assistant.proposal.confirm')}
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void decide('cancel')}>
            <X className="size-4" aria-hidden="true" />
            {t('assistant.proposal.cancel')}
          </Button>
          <span className="text-xs text-[var(--color-muted)]">
            {t('assistant.proposal.notYetSaved')}
          </span>
        </div>
      )}

      {state.status === 'CONFIRMED' && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-[var(--color-success,#047857)]">
          <CheckCircle2 className="size-4" aria-hidden="true" />
          {t('assistant.proposal.confirmed')}
          {destination && (
            <Link className="underline underline-offset-4" to={destination.path}>
              {destination.label}
            </Link>
          )}
        </p>
      )}

      {state.status === 'CANCELLED' && (
        <p className="mt-3 text-sm text-[var(--color-muted)]">{t('assistant.proposal.cancelled')}</p>
      )}

      {state.status === 'EXPIRED' && (
        <p className="mt-3 text-sm text-[var(--color-muted)]">{t('assistant.proposal.expired')}</p>
      )}

      {(state.status === 'FAILED' || state.refusal) && (
        <p role="alert" className="mt-3 flex items-start gap-1.5 text-sm text-[var(--color-danger)]">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {state.refusal ?? t('assistant.proposal.failed')}
        </p>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm text-[var(--color-danger)]">{error}</p>
      )}
    </div>
  )
}

function humanise(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, (first) => first.toUpperCase())
}

/** Arguments are whatever the model produced, so this renders any JSON without throwing. */
function render(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return JSON.stringify(value)
}
